import React, { useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { config, useTransition } from 'react-spring'

import { SIDE_PANEL_PORTAL_ID } from '~/constants'

import SidePanel from '../SidePanel'
import { Overlay } from '../SidePanel/styles'

import { Container } from './styles'

export const SidePanelContainer: React.FC<SidePanelContainerProps> = ({
  message,
  onClose = () => undefined
}) => {
  const modalRef = useRef<HTMLDialogElement>(null)
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(
    null
  )
  const transitionItems = [message]

  useEffect(() => {
    setPortalTarget(document.getElementById(SIDE_PANEL_PORTAL_ID))
  }, [])

  const messagesWithTransitions = useTransition(transitionItems, {
    keys: item => item.id,
    from: { transform: 'translate3d(100%, 0, 0)' },
    enter: { transform: 'translate3d(0%, 0, 0)' },
    leave: { transform: 'translate3d(100%, 0, 0)' },
    config: {
      ...config.default,
      tension: 280,
      friction: 30
    }
  })

  const overlayWithTransitions = useTransition(transitionItems, {
    keys: item => item.id,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      ...config.default,
      tension: 280,
      friction: 30
    }
  })

  const handleClose = useCallback(
    (callback?: () => void) => {
      const elementToFocus = previouslyFocusedRef.current
      const modalEl = modalRef.current
      if (modalEl?.open) {
        modalEl.close()
      }
      onClose(callback)
      setTimeout(() => {
        if (
          elementToFocus &&
          typeof elementToFocus.focus === 'function' &&
          document.contains(elementToFocus)
        ) {
          elementToFocus.focus()
        }
      }, 0)
    },
    [onClose]
  )

  useEffect(() => {
    if (!message || !portalTarget) return

    const modalEl = modalRef.current
    if (!modalEl || modalEl.open) return

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null
    modalEl.showModal()

    return () => {
      if (modalEl.open) modalEl.close()
    }
  }, [message?.id, portalTarget])

  useEffect(() => {
    if (!portalTarget || !message) return

    const modalEl = modalRef.current
    if (!modalEl) return

    const handleCancel = (event: Event) => {
      event.preventDefault()
      handleClose()
    }

    modalEl.addEventListener('cancel', handleCancel)

    return () => {
      modalEl.removeEventListener('cancel', handleCancel)
    }
  }, [message.id, portalTarget, handleClose])

  if (!portalTarget) {
    return null
  }

  return createPortal(
    <Container
      ref={modalRef}
      aria-labelledby={`side-panel-${message?.id}-title`}
    >
      {overlayWithTransitions((styles, item) => (
        <Overlay
          aria-label="Close side panel overlay"
          key={`overlay-${item.id}`}
          onClick={() => handleClose(item.callbackRemove)}
          tabIndex={-1}
          style={styles}
        />
      ))}

      {messagesWithTransitions((styles, item) => (
        <SidePanel
          key={item.id}
          message={item}
          onClose={handleClose}
          style={styles}
        />
      ))}
    </Container>,
    portalTarget
  )
}
